import { Link } from "react-router-dom";
import { useLocation } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import {
  masonryServices,
  pavingServices,
  concreteServices,
  waterproofingServices,
  masonrySiblingMap,
  type ServiceItem,
} from "@/data/servicesData";

type ServiceCategory = "masonry" | "paving" | "concrete" | "waterproofing";

interface CategoryData {
  title: string;
  hub: string;
  siblings: ServiceItem[];
}

const categories: Record<ServiceCategory, CategoryData> = {
  masonry: {
    title: "Masonry Services",
    hub: "/",
    siblings: masonryServices,
  },
  paving: {
    title: "Paving Services",
    hub: "/paving-services",
    siblings: pavingServices,
  },
  concrete: {
    title: "Concrete Services",
    hub: "/concrete-services",
    siblings: concreteServices,
  },
  waterproofing: {
    title: "Waterproofing Services",
    hub: "/waterproofing-services",
    siblings: waterproofingServices,
  },
};

interface RelatedServicesProps {
  currentCategory: ServiceCategory;
  hubOverride?: { href: string; label: string };
}

export function RelatedServices({ currentCategory, hubOverride }: RelatedServicesProps) {
  const location = useLocation();
  const currentPath = location.pathname.replace(/\/$/, "") || "/";
  const cat = categories[currentCategory];

  // Dedupe by link, exclude the current page, and cap at 6 siblings.
  // Masonry pages use the curated sibling map when an entry exists.
  const curated = currentCategory === "masonry" ? masonrySiblingMap[currentPath] : undefined;
  const seen = new Set<string>();
  const siblings = curated
    ? curated
        .map((link) => cat.siblings.find((s) => s.link === link))
        .filter((s): s is ServiceItem => !!s && s.link !== currentPath)
    : cat.siblings.filter((s) => {
        if (s.link === currentPath) return false;
        if (seen.has(s.link)) return false;
        seen.add(s.link);
        return true;
      });

  const hubHref = hubOverride?.href ?? cat.hub;
  const hubLabel = hubOverride?.label ?? (currentCategory === "masonry" ? "Masonry Contractor in North Jersey" : `All ${cat.title}`);
  const hubSubLabel = hubOverride || currentCategory === "masonry" ? "Back to homepage" : `View ${cat.title.replace(/ Services$/, "")} hub`;

  const showHub = currentPath !== hubHref;
  const headingLabel = cat.title.replace(/ Services$/, "");

  return (
    <section className="py-16 bg-muted/30">
      <div className="container mx-auto px-4">
        <h2 className="text-2xl md:text-3xl font-bold text-center mb-10 text-foreground">
          More {headingLabel} Services
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {showHub && (
            <div className="bg-card rounded-xl border border-border p-6 space-y-4">
              <Link
                to={hubHref}
                className="text-lg font-bold text-secondary hover:underline flex items-center gap-1"
              >
                {hubLabel} <ArrowRight className="h-4 w-4" />
              </Link>
              <ul className="space-y-2">
                <li>
                  <Link
                    to={hubHref}
                    className="text-muted-foreground hover:text-secondary transition-colors text-sm flex items-center gap-1.5"
                  >
                    <ArrowRight className="h-3 w-3 shrink-0" />
                    {hubSubLabel}
                  </Link>
                </li>
              </ul>
            </div>
          )}
          {siblings.map((s) => (
            <div
              key={s.link}
              className="bg-card rounded-xl border border-border p-6 space-y-4"
            >
              <Link
                to={s.link}
                className="text-lg font-bold text-secondary hover:underline flex items-center gap-1"
              >
                {s.title} <ArrowRight className="h-4 w-4" />
              </Link>
              <ul className="space-y-2">
                <li>
                  <Link
                    to={s.link}
                    className="text-muted-foreground hover:text-secondary transition-colors text-sm flex items-center gap-1.5"
                  >
                    <ArrowRight className="h-3 w-3 shrink-0" />
                    Learn more
                  </Link>
                </li>
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
